import { EQUIPMENT_CATEGORIES, SKU } from 'types/sku';

/**
 * The Inventory Summary category filter (ClickUp 86bbmcv2b).
 *
 * "Item" is the odd one out: it is the SKU type for cups rather than an
 * equipment category. The ticket lists all six together and they read as one
 * concept to the person filtering, so they share a list here; the two meanings
 * separate again in resolveCategorySkuIds below.
 */
export const ITEM_CATEGORY = 'Item';

export const INVENTORY_CATEGORIES = [
  ITEM_CATEGORY,
  ...EQUIPMENT_CATEGORIES,
] as const;

/**
 * Sent when a filter is active but matches no SKU at all.
 *
 * The counts API treats an empty sku list as "no filter" and returns every row
 * (counts.service.ts processCountData), so a category matching nothing has to
 * be sent as an id that cannot match rather than as an empty list, or the
 * table would show everything instead of nothing.
 */
export const NO_MATCHING_SKU = '__no_matching_sku__';

/**
 * Resolves the filter selections down to the SKU ids the counts API accepts,
 * which is the only thing it can filter on.
 *
 * Selected SKUs and selected categories are a union, not an intersection:
 * picking "Blue Tote" and "Return Bin" means both, not the SKUs that are
 * somehow both at once, which would always be none.
 *
 * Returns undefined when nothing is selected, so the caller omits the
 * parameter entirely and the API applies no filter.
 */
export const resolveCategorySkuIds = (
  selectedSkuIds: string[] | undefined,
  selectedCategories: string[] | undefined,
  itemSkus: SKU[] | undefined,
  equipmentSkus: SKU[] | undefined,
): string[] | undefined => {
  const hasSkus = Boolean(selectedSkuIds?.length);
  const hasCategories = Boolean(selectedCategories?.length);

  if (!hasSkus && !hasCategories) {
    return undefined;
  }

  const ids = new Set<string>(selectedSkuIds || []);

  if (hasCategories) {
    const categories = selectedCategories as string[];

    if (categories.includes(ITEM_CATEGORY)) {
      (itemSkus || []).forEach((sku) => ids.add(String(sku._id)));
    }

    const equipmentCategories = categories.filter(
      (category) => category !== ITEM_CATEGORY,
    );

    if (equipmentCategories.length > 0) {
      (equipmentSkus || [])
        .filter(
          (sku) => sku.category && equipmentCategories.includes(sku.category),
        )
        .forEach((sku) => ids.add(String(sku._id)));
    }
  }

  return ids.size > 0 ? Array.from(ids) : [NO_MATCHING_SKU];
};
